// Per-tenant storefront referral / affiliate code capture (Phase 2).
//
// When a visitor arrives at a tenant store with `?ref=AFF-XXXX` (per-tenant
// affiliate) or `?ref=REF-XXXX` (per-tenant customer referral), we persist
// the code to localStorage with a 30-day TTL. At checkout time, the cart
// payload reads the stored code and forwards it as `affiliateRef` or
// `referralCode` so the API can record the commission/reward after the
// order is created.
//
// IMPORTANT: This is intentionally separate from `nx_partner_ref` used in
// `pages/signup.tsx` (Phase 1), which is the PLATFORM partner program for
// nexuscart.co merchant signup. The two systems live at different scopes
// (per-tenant store vs platform) and must not share state.

const STORAGE_KEY = "nx_store_ref";
const TTL_MS = 30 * 24 * 60 * 60 * 1000;
const AFF_RE = /^AFF-[A-Z0-9]+$/i;
const REF_RE = /^REF-[A-Z0-9]+$/i;

interface StoredRef {
  code: string;
  ts: number;
}

function readStored(): StoredRef | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredRef;
    if (!parsed?.code || typeof parsed.ts !== "number") return null;
    if (Date.now() - parsed.ts > TTL_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    if (!AFF_RE.test(parsed.code) && !REF_RE.test(parsed.code)) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeStored(code: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ code, ts: Date.now() }));
  } catch {
    /* ignore quota / disabled storage */
  }
}

/**
 * Reads `?ref=` from the current URL on first paint, validates the prefix
 * (AFF- or REF-), persists it, and (for affiliate codes) fires a fire-and-
 * forget click-track ping. Safe to call repeatedly — last valid code wins.
 */
export function captureStoreRef(): void {
  if (typeof window === "undefined") return;
  try {
    const url = new URL(window.location.href);
    const fromUrl = (url.searchParams.get("ref") || "").trim().slice(0, 50);
    if (!fromUrl) return;
    if (!AFF_RE.test(fromUrl) && !REF_RE.test(fromUrl)) return;

    writeStored(fromUrl);

    if (AFF_RE.test(fromUrl)) {
      const apiBase = `${import.meta.env.BASE_URL || "/"}api`;
      fetch(`${apiBase}/affiliates/track/click`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ code: fromUrl }),
      }).catch(() => {
        /* best-effort; visitor must never see an error */
      });
    }
  } catch {
    /* malformed URL — ignore */
  }
}

/**
 * Returns whichever ref-shaped fields should ride along on the order
 * payload. Empty object when no valid stored code.
 */
export function getStoreRef(): { affiliateRef?: string; referralCode?: string } {
  const stored = readStored();
  if (!stored) return {};
  if (AFF_RE.test(stored.code)) return { affiliateRef: stored.code };
  if (REF_RE.test(stored.code)) return { referralCode: stored.code };
  return {};
}

export function clearStoreRef(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
